import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import * as Y from 'yjs';
import { Awareness, encodeAwarenessUpdate, applyAwarenessUpdate } from 'y-protocols/awareness';

/**
 * R20 (round-6) + W3.1 / W3.2 (v1.1) — collaborative textarea
 * backed by a real Yjs CRDT, with presence + cursors + typing
 * indicators via the y-protocols awareness layer.
 *
 * Wire protocol (matches what the Rust `ws_crdt::CrdtRelay` relays
 * verbatim):
 *
 *   first byte = message type
 *     0x00 → Yjs doc update (applied via Y.applyUpdate)
 *     0x01 → Awareness update (applied via applyAwarenessUpdate)
 *   remaining bytes = the encoded update.
 *
 * Every reviewer subscribed to the same approval shares one Y.Doc
 * + one Awareness instance. Local edits flow through `Y.Text` so
 * concurrent typing at different cursor positions merges cleanly
 * (RGA). Awareness broadcasts `{ user, cursor, typing_until }` on
 * every input / selection change so peers render live cursors and
 * "typing…" badges above the textarea.
 */
export interface PeerIdentity {
  id: string;
  name: string;
  color?: string;
}

export interface CollaborativeTextareaProps {
  /**
   * Stable identifier for the Yjs "room". Every reviewer that joins
   * with the same `room` shares the same Y.Doc + awareness channel.
   *
   * Legacy callers passed `approvalId` — still honoured, but prefer
   * `room` for new surfaces (T5.5 unlocks CRDT on project
   * descriptions, checkpoint comments, steering-file bodies).
   */
  room?: string;
  /** Back-compat alias for `room`. Used by the Approvals surface. */
  approvalId?: string;
  initial: string;
  wsUrl: string;
  onChange?: (value: string) => void;
  rows?: number;
  ariaLabel?: string;
  /** W3.2 — local peer identity published via the awareness layer. */
  peer?: PeerIdentity;
  /**
   * T5.2 (v2.0) — when true, render the "N reviewers here" compact
   * counter above the textarea even when no remote peer is
   * currently typing. Useful on approval headers where the count
   * is the signal (not the typing indicator).
   */
  showPresenceCounter?: boolean;
}

interface AwarenessState {
  user?: { id: string; name: string; color: string };
  cursor?: { index: number; length: number };
  typing_until?: number;
}

const MSG_SYNC = 0;
const MSG_AWARENESS = 1;

function defaultPeer(): PeerIdentity {
  const id = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto?.randomUUID
    ? globalThis.crypto!.randomUUID!()
    : `anon-${Math.random().toString(16).slice(2, 10)}`;
  return { id, name: 'You', color: '#34d399' };
}

function hashColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) & 0xffffffff;
  const hue = Math.abs(h) % 360;
  return `hsl(${hue} 70% 55%)`;
}

function framed(type: number, body: Uint8Array): Uint8Array {
  const out = new Uint8Array(1 + body.length);
  out[0] = type;
  out.set(body, 1);
  return out;
}

export function CollaborativeTextarea({
  room,
  approvalId,
  initial,
  wsUrl,
  onChange,
  rows = 6,
  ariaLabel,
  peer,
  showPresenceCounter = false,
}: CollaborativeTextareaProps): JSX.Element {
  // T5.5 — prefer `room`; fall back to `approvalId` for legacy
  // callers so the Approvals surface keeps working.
  const roomId = room ?? approvalId ?? 'default';
  const resolvedPeer = useMemo<PeerIdentity>(
    () => peer ?? defaultPeer(),
    // Stabilise on roomId — a new room can re-derive the peer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roomId, peer?.id],
  );
  const [value, setValue] = useState(initial);
  const [peers, setPeers] = useState<Array<AwarenessState & { clientId: number }>>([]);
  const docRef = useRef<Y.Doc | null>(null);
  const awarenessRef = useRef<Awareness | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const doc = new Y.Doc();
    docRef.current = doc;
    const ytext = doc.getText('rationale');
    const awareness = new Awareness(doc);
    awarenessRef.current = awareness;
    awareness.setLocalState({
      user: {
        id: resolvedPeer.id,
        name: resolvedPeer.name,
        color: resolvedPeer.color ?? hashColor(resolvedPeer.id),
      },
      cursor: { index: 0, length: 0 },
      typing_until: 0,
    });

    if (initial) {
      ytext.insert(0, initial);
    }

    const observer = () => {
      const next = ytext.toString();
      setValue(next);
      onChange?.(next);
    };
    ytext.observe(observer);

    const awarenessObserver = () => {
      const states: Array<AwarenessState & { clientId: number }> = [];
      awareness.getStates().forEach((state, clientId) => {
        if (clientId === awareness.clientID) return; // skip self
        states.push({ ...(state as AwarenessState), clientId });
      });
      setPeers(states);
    };
    awareness.on('change', awarenessObserver);

    const onDocUpdate = (update: Uint8Array, origin: unknown) => {
      if (origin === 'remote') return;
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(framed(MSG_SYNC, update));
      }
    };
    doc.on('update', onDocUpdate);

    const onAwarenessUpdate = (
      _changes: { added: number[]; updated: number[]; removed: number[] },
      origin: unknown,
    ) => {
      if (origin === 'remote') return;
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        const update = encodeAwarenessUpdate(awareness, [awareness.clientID]);
        ws.send(framed(MSG_AWARENESS, update));
      }
    };
    awareness.on('update', onAwarenessUpdate);

    let ws: WebSocket | null = null;
    try {
      // T5.5 — route by roomId. `approval=` stays the query key so
      // the server's `CrdtScope` schema doesn't need to change today.
      ws = new WebSocket(`${wsUrl}?approval=${roomId}`);
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;
      ws.onmessage = (ev: MessageEvent<ArrayBuffer | string>) => {
        if (typeof ev.data === 'string') {
          if (ytext.length === 0) {
            doc.transact(() => ytext.insert(0, ev.data as string), 'remote');
          }
          return;
        }
        const bytes = new Uint8Array(ev.data);
        if (bytes.length === 0) return;
        const type = bytes[0];
        const body = bytes.subarray(1);
        try {
          if (type === MSG_SYNC) {
            Y.applyUpdate(doc, body, 'remote');
          } else if (type === MSG_AWARENESS) {
            applyAwarenessUpdate(awareness, body, 'remote');
          }
        } catch {
          /* malformed frame — ignore */
        }
      };
      ws.onerror = () => {
        /* silent: degrade to local-only mode */
      };
      ws.onopen = () => {
        const state = Y.encodeStateAsUpdate(doc);
        ws?.send(framed(MSG_SYNC, state));
        const awarenessFrame = encodeAwarenessUpdate(awareness, [awareness.clientID]);
        ws?.send(framed(MSG_AWARENESS, awarenessFrame));
      };
    } catch {
      /* WS unavailable — local-only mode. */
    }

    return () => {
      ytext.unobserve(observer);
      awareness.off('change', awarenessObserver);
      awareness.off('update', onAwarenessUpdate);
      doc.off('update', onDocUpdate);
      awareness.destroy();
      ws?.close();
      doc.destroy();
      wsRef.current = null;
      docRef.current = null;
      awarenessRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, wsUrl, resolvedPeer.id]);

  function markTyping(): void {
    const awareness = awarenessRef.current;
    if (!awareness) return;
    const current = awareness.getLocalState() as AwarenessState | null;
    awareness.setLocalState({
      ...(current ?? {}),
      typing_until: Date.now() + 1500,
    });
  }

  function updateCursor(): void {
    const awareness = awarenessRef.current;
    const el = textareaRef.current;
    if (!awareness || !el) return;
    const current = awareness.getLocalState() as AwarenessState | null;
    awareness.setLocalState({
      ...(current ?? {}),
      cursor: {
        index: el.selectionStart,
        length: Math.max(0, el.selectionEnd - el.selectionStart),
      },
    });
  }

  function handleInput(e: React.ChangeEvent<HTMLTextAreaElement>): void {
    const next = e.target.value;
    const doc = docRef.current;
    if (!doc) {
      setValue(next);
      onChange?.(next);
      return;
    }
    const ytext = doc.getText('rationale');
    const current = ytext.toString();
    if (next === current) return;

    let start = 0;
    while (
      start < current.length
      && start < next.length
      && current.charCodeAt(start) === next.charCodeAt(start)
    ) {
      start++;
    }
    let endOld = current.length;
    let endNew = next.length;
    while (
      endOld > start
      && endNew > start
      && current.charCodeAt(endOld - 1) === next.charCodeAt(endNew - 1)
    ) {
      endOld--;
      endNew--;
    }
    doc.transact(() => {
      if (endOld > start) {
        ytext.delete(start, endOld - start);
      }
      if (endNew > start) {
        ytext.insert(start, next.slice(start, endNew));
      }
    });
    markTyping();
    updateCursor();
  }

  const now = Date.now();
  const typingPeers = peers.filter(
    (p) => p.user && typeof p.typing_until === 'number' && p.typing_until > now,
  );

  // T5.2 (v2.0) — reviewer count (self + remote peers). Rendered
  // unconditionally when `showPresenceCounter` is true so surfaces
  // like the approval-detail header always expose "X reviewers
  // here" even before anyone types.
  const reviewerCount = 1 + peers.filter((p) => p.user).length;

  return (
    <div className="flex flex-col gap-1">
      {showPresenceCounter && (
        <div
          className="flex items-center gap-2 text-2xs text-fg-muted"
          aria-live="polite"
        >
          <span
            aria-hidden="true"
            className="inline-block h-1.5 w-1.5 rounded-full bg-status-success"
          />
          <span>
            {reviewerCount === 1
              ? 'Only you'
              : `${reviewerCount} reviewers here`}
          </span>
          {/* Avatar stack — up to 4 colored dots, then "+N". */}
          <div className="flex -space-x-1">
            {peers.slice(0, 4).map((p) =>
              p.user ? (
                <span
                  key={`avatar-${p.clientId}`}
                  className="inline-block h-3 w-3 rounded-full border border-bg-base"
                  style={{ backgroundColor: p.user.color }}
                  title={p.user.name}
                />
              ) : null,
            )}
            {peers.length > 4 && (
              <span className="ml-1 text-fg-muted">+{peers.length - 4}</span>
            )}
          </div>
        </div>
      )}
      {(peers.length > 0 || typingPeers.length > 0) && (
        <div
          className="flex flex-wrap items-center gap-1 text-2xs text-fg-muted"
          aria-live="polite"
        >
          {peers.map((p) =>
            p.user ? (
              <span
                key={p.clientId}
                className="inline-flex items-center gap-1 rounded-full border border-border-subtle px-2 py-0.5"
                title={`${p.user.name} · cursor ${p.cursor?.index ?? 0}`}
              >
                <span
                  aria-hidden="true"
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: p.user.color }}
                />
                {p.user.name}
                {p.typing_until && p.typing_until > now ? (
                  <span className="ml-1 italic text-fg-secondary">typing…</span>
                ) : null}
              </span>
            ) : null,
          )}
        </div>
      )}
      <textarea
        ref={textareaRef}
        aria-label={ariaLabel ?? `Collaborative rationale for room ${roomId}`}
        rows={rows}
        value={value}
        onChange={handleInput}
        onSelect={updateCursor}
        onKeyDown={markTyping}
        className="w-full resize-y rounded border border-border-subtle bg-bg-raised p-2 text-sm text-fg-primary"
      />
    </div>
  );
}
